/**
 * ReviewBanner - Alert banner for CO review decisions
 *
 * Shows decision notes from the CO review and unresolved comment count.
 * Amber for changes_requested, red for rejected.
 * Dismissible via local state (not persisted). Positioned between
 * ExtractionStatusBar and KYCWizard in KYCFormPanel.
 */

import React, { useState } from 'react';
import { AlertTriangle, XCircle, CheckCircle2, X } from 'lucide-react';
import type { KYCReview, KYCReviewComment } from '../../../types/complianceWorkflow';

interface ReviewBannerProps {
  review: KYCReview;
  comments: KYCReviewComment[];
}

const STYLES = {
  changes_requested: {
    bg: 'bg-amber-50 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    title: 'Changes Requested by Compliance',
    titleColor: 'text-amber-800',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-200 text-amber-800',
    dismissColor: 'text-amber-400 hover:text-amber-600',
  },
  reject: {
    bg: 'bg-red-50 border-red-200',
    icon: XCircle,
    iconColor: 'text-red-600',
    title: 'KYC Profile Rejected by Compliance',
    titleColor: 'text-red-800',
    textColor: 'text-red-700',
    badgeBg: 'bg-red-200 text-red-800',
    dismissColor: 'text-red-400 hover:text-red-600',
  },
  approve: {
    bg: 'bg-green-50 border-green-200',
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    title: 'KYC Profile Approved',
    titleColor: 'text-green-800',
    textColor: 'text-green-700',
    badgeBg: 'bg-green-200 text-green-800',
    dismissColor: 'text-green-400 hover:text-green-600',
  },
} as const;

const ReviewBanner: React.FC<ReviewBannerProps> = ({ review, comments }) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const style = STYLES[review.decision as keyof typeof STYLES] ?? STYLES.changes_requested;
  const Icon = style.icon;
  const unresolvedCount = comments.filter((c) => !c.is_resolved).length;

  return (
    <div className={`${style.bg} border-b px-4 py-3 flex items-start gap-3`}>
      <Icon className={`w-5 h-5 ${style.iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${style.titleColor}`}>
          {style.title}
        </p>
        {review.rejection_category && review.decision === 'reject' && (
          <p className={`text-xs ${style.textColor} mt-0.5`}>
            Category: {review.rejection_category.replace(/_/g, ' ')}
          </p>
        )}
        {review.decision_notes && (
          <p className={`text-sm ${style.textColor} mt-1`}>{review.decision_notes}</p>
        )}
        {unresolvedCount > 0 && (
          <span className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${style.badgeBg}`}>
            {unresolvedCount} unresolved comment{unresolvedCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className={`shrink-0 p-1 ${style.dismissColor} transition-colors rounded`}
        aria-label="Dismiss banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ReviewBanner;
